import React, { useState } from 'react';
import { useAuth } from '@clerk/clerk-react';

// Authentication and retry constants
const TOKEN_TIMEOUT_MS = 10000; // 10 second timeout for token retrieval
const MAX_RETRY_ATTEMPTS = 2; // Maximum retry attempts for token retrieval
const RETRY_DELAY_BASE_MS = 1000; // Base delay for retry attempts
const JWT_TOKEN_PARTS = 3; // Expected parts in a JWT token

const Search = ({ subreddits, onSearchResults, onLimitReached, onSearchStart, onSearchEnd }) => {
  const { getToken, isSignedIn, signOut } = useAuth();
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  

  

  
  const [query, setQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  // Enhanced token retrieval with retry logic
  const getValidToken = async (retryCount = 0) => {
    try {
      if (!isSignedIn) {
        throw new Error('User not signed in');
      }

      const token = await getToken({ timeout: TOKEN_TIMEOUT_MS });
      
      if (!token) {
        throw new Error('No token received from Clerk');
      }

      // Basic token validation - check if it's a valid JWT format
      const tokenParts = token.split('.');
      if (tokenParts.length !== JWT_TOKEN_PARTS) {
        throw new Error('Invalid token format');
      }

      return token;
    } catch (error) {
      console.error(`Token retrieval attempt ${retryCount + 1} failed:`, error);
      
      // Retry up to maximum attempts with increasing delay
      if (retryCount < MAX_RETRY_ATTEMPTS) {
        await new Promise(resolve => setTimeout(resolve, (retryCount + 1) * RETRY_DELAY_BASE_MS));
        return getValidToken(retryCount + 1);
      }
      
      throw error;
    }
  };

  // Enhanced API call with automatic retry on 401
  const makeAuthenticatedRequest = async (url, options, retryCount = 0) => {
    try {
      const response = await fetch(url, options);
      
      // If 401 and we haven't retried yet, try to get a fresh token
      if (response.status === 401 && retryCount === 0) {
        
        // Get a fresh token
        const freshToken = await getValidToken();
        
        // Retry the request with the fresh token
        const freshOptions = {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${freshToken}`
          }
        };
        
        return makeAuthenticatedRequest(url, freshOptions, 1);
      }
      
      return response;
    } catch (error) {
      console.error('Request failed:', error);
      throw error;
    }
  };

  const handleSearch = async (event) => {
    if (event) event.preventDefault();
    
    if (!query.trim()) {
        setError('Please enter a search query.');
        return;
    };

    if (!isSignedIn) {
      setError('Please sign in to search.');
      return;
    }
  
    setIsLoading(true);
    setError('');
    
    // Notify parent that search is starting
    if (onSearchStart) {
      onSearchStart();
    }
  
    try {
      // Format the subreddits to match backend expectations
      const formattedSubreddits = subreddits.map((sub) => ({
        subreddit: sub.subreddit_name || sub.subreddit,
      }));
      
      const requestUrl = `${baseUrl}/v1/search-within-subreddits`;
      
      // Get the authentication token with enhanced error handling
      const token = await getValidToken();
      
      // Make the request with retry logic
      const response = await makeAuthenticatedRequest(requestUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ subreddits: formattedSubreddits, query }),
      });

      if (response.status === 429) {
          let errorMsg = 'Monthly search limit reached.';
          try {
              const errorData = await response.json();
              errorMsg = errorData.error || errorMsg;
          } catch(e) {
              console.error("Could not parse 429 response body", e);
          }
          setError(errorMsg);
          if (typeof onLimitReached === 'function') {
              onLimitReached();
          }
          setIsLoading(false);
          return;
      }
  
      if (!response.ok) {
        let errorMsg = `Search failed: ${response.statusText} (${response.status})`;
         try {
            const errorData = await response.json();
            errorMsg = errorData.error || errorMsg;
        } catch (e) { /* Ignore if body isn't JSON */ }
        console.error("Search fetch error:", errorMsg);
        throw new Error(errorMsg);
      }
  
      const results = await response.json();
  
      if (onSearchResults) {
        onSearchResults(results);
      }
    } catch (err) {
      console.error("handleSearch Error:", err);
      setError(err.message || 'An unknown error occurred during the search.');
    } finally {
      setIsLoading(false);
      
      // Notify parent that search has ended
      if (onSearchEnd) {
        onSearchEnd();
      }
    }
  };

  const handleInputChange = (e) => {
    setQuery(e.target.value);
    if (error) setError('');
  };

  return (
    <div className="mb-4">
      <form onSubmit={handleSearch} className="flex items-start gap-2">
        <div className='flex-grow'>
            <input
                type="text"
                value={query}
                onChange={handleInputChange}
                placeholder="Search within these subreddits..."
                className={`w-full bg-gray-700 text-white rounded-lg px-4 py-2 focus:outline-none focus:ring-2 ${error ? 'ring-red-500' : 'focus:ring-indigo-500'}`}
                disabled={isLoading}
            />
            {error && <p className="text-red-500 text-sm mt-1 ml-1">{error}</p>}
        </div>
        <button
            type="submit"
            className="bg-indigo-600 hover:bg-indigo-700 text-white font-medium py-2 px-5 rounded-lg transition-colors disabled:opacity-50 flex-shrink-0 h-[42px]"
            disabled={isLoading}
        >
            {isLoading ? 'Searching...' : 'Search'}
        </button>
      </form>
    </div>
  );
};

export default Search;
